import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RHFPhoneInput } from '@billda/shared-ui';
import { useCaptureProfile } from '../hooks/useCaptureProfile';
import {
  profileCaptureSchema,
  ProfileCaptureFormData,
} from '../types/profileCaptureSchema';

interface ProfileCaptureFormProps {
  invitationId: string;
  organisationName: string;
  onSuccess: () => void;
  onError: (message: string) => void;
  onDecline: () => void;
  initialGivenName?: string;
  initialFamilyName?: string;
}

const ProfileCaptureForm: React.FC<ProfileCaptureFormProps> = ({
  invitationId,
  organisationName,
  onSuccess,
  onError,
  onDecline,
  initialGivenName,
  initialFamilyName,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      givenName: initialGivenName ?? '',
      familyName: initialFamilyName ?? '',
      primaryPhoneNumber: '',
    },
    resolver: yupResolver(profileCaptureSchema) as any,
    mode: 'onChange',
  });

  const { mutate: captureProfile, isPending } = useCaptureProfile();

  const onSubmit = (data: any) => {
    const formData: ProfileCaptureFormData = {
      givenName: data.givenName,
      familyName: data.familyName,
      primaryPhoneNumber: data.primaryPhoneNumber,
    };
    captureProfile(
      { invitationId, data: formData },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: error => {
          onError(
            error instanceof Error
              ? error.message
              : 'Failed to capture profile information'
          );
        },
      }
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          data-testid="profile-capture-title"
          sx={{ color: 'primary.main', fontWeight: 600 }}
        >
          Complete Your Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please provide your details to join {organisationName}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Stack
            spacing={2}
            direction={{ xs: 'column', lg: 'row' }}
            data-testid="profile-name-row"
          >
            <Box>
              <Controller
                name="givenName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.givenName}
                    helperText={errors.givenName?.message}
                    disabled={isPending}
                    autoComplete="given-name"
                    data-testid="profile-given-name-input"
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="familyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.familyName}
                    helperText={errors.familyName?.message}
                    disabled={isPending}
                    autoComplete="family-name"
                    data-testid="profile-family-name-input"
                  />
                )}
              />
            </Box>
          </Stack>

          <RHFPhoneInput
            name="primaryPhoneNumber"
            control={control}
            label="Phone Number"
            fullWidth
            disabled={isPending}
            required
            data-testid="profile-phone-input"
          />

          <Alert
            data-testid="profile-consent-section"
            severity="info"
            sx={{ mb: 2 }}
          >
            We&apos;ll share your name and contact details with{' '}
            {organisationName} so they can add you to their organisation.
          </Alert>

          <Stack
            justifyContent="end"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
          >
            <Button
              variant="outlined"
              onClick={onDecline}
              data-testid="profile-consent-decline-button"
            >
              Decline
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || isPending}
              data-testid="profile-submit-button"
            >
              {isPending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Accept and Continue'
              )}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default ProfileCaptureForm;
